<template>
    <validation-observer ref="refForm">

        <b-form
            @submit.prevent="onSubmit"
        >

            <b-row>
                <b-col md="12">
                    <b-card footer-tag="footer">

                        <b-row>

                            <!-- READ ONLY: SURVEY CONTENT -->
                            <b-col cols="12">
                                <b-alert variant="info" :show="true">
                                    <div class="alert-body">
                                        El puntaje, el comentario y la fecha provienen de la encuesta respondida por el cliente
                                        y no se pueden editar desde aquí.
                                    </div>
                                </b-alert>
                            </b-col>

                            <!-- SCORE -->
                            <b-col cols="12" md="4">
                                <b-form-group>
                                    <template #label>
                                        <strong>PUNTAJE</strong>
                                    </template>
                                    <div v-if="testimonialData.score">
                                        <ul class="unstyled-list list-inline mb-0">
                                            <li
                                                v-for="star in 5"
                                                :key="star"
                                                class="ratings-list-item"
                                            >
                                                <feather-icon
                                                    icon="StarIcon"
                                                    size="20"
                                                    class="mr-25"
                                                    :class="[{'fill-current': star <= testimonialData.score}, star <= testimonialData.score ? 'text-warning' : 'text-muted']"
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                    <span v-else class="text-muted">Sin puntaje registrado</span>
                                </b-form-group>
                            </b-col>

                            <!-- COMPLETION DATE -->
                            <b-col cols="12" md="4">
                                <b-form-group>
                                    <template #label>
                                        <strong>FECHA DE LA ENCUESTA</strong>
                                    </template>
                                    <span>{{ testimonialData.completion_date ? moment(testimonialData.completion_date).format('DD/MM/YYYY') : '-' }}</span>
                                </b-form-group>
                            </b-col>

                            <!-- BOOKING CODE -->
                            <b-col cols="12" md="4">
                                <b-form-group>
                                    <template #label>
                                        <strong>CÓDIGO DE RESERVA</strong>
                                    </template>
                                    <span>{{ testimonialData.code || '-' }}</span>
                                </b-form-group>
                            </b-col>

                            <!-- COMMENTS -->
                            <b-col cols="12">
                                <b-form-group>
                                    <template #label>
                                        <strong>COMENTARIO</strong>
                                    </template>
                                    <b-form-textarea
                                        :value="testimonialData.comments"
                                        rows="4"
                                        readonly
                                    />
                                </b-form-group>
                            </b-col>

                            <b-col cols="12"><hr></b-col>

                            <!-- ANONYMOUS -->
                            <b-col cols="12">
                                <b-form-group>
                                    <template #label>
                                        <strong>PUBLICAR COMO ANÓNIMO</strong>
                                    </template>
                                    <b-form-checkbox
                                        id="is_anonymous"
                                        v-model="formData.is_anonymous"
                                        switch
                                    >
                                        En la web aparece como <strong>Anónimo</strong>
                                    </b-form-checkbox>
                                    <small class="text-muted">
                                        Actívalo cuando el cliente pidió reservar sin dar su identidad.
                                        Tiene prioridad sobre el alias y sobre el nombre del cliente.
                                    </small>
                                </b-form-group>
                            </b-col>

                            <!-- ALIAS -->
                            <b-col cols="12" md="8">
                                <b-form-group
                                    label-for="testimonial_alias"
                                >
                                    <template #label>
                                        <strong>NOMBRE PÚBLICO (ALIAS)</strong>
                                    </template>
                                    <b-form-input
                                        id="testimonial_alias"
                                        v-model="formData.testimonial_alias"
                                        :disabled="formData.is_anonymous"
                                        placeholder="Dejar vacío para usar el nombre del cliente ..."
                                        maxlength="150"
                                        trim
                                    />
                                    <small class="textarea-counter-value float-right">
                                        <strong class="char-count">{{ formData.testimonial_alias ? formData.testimonial_alias.length : 0 }} / 150</strong>
                                    </small>
                                    <small class="text-muted" v-if="formData.is_anonymous">
                                        Sin efecto mientras el testimonio sea anónimo.
                                    </small>
                                    <small class="text-muted" v-else>
                                        Si se deja vacío se publica
                                        <strong>{{ testimonialData.client_fullname }}</strong>.
                                    </small>
                                </b-form-group>
                            </b-col>

                            <!-- ORDER -->
                            <b-col cols="12" md="4">
                                <b-form-group
                                    label-for="testimonial_order"
                                >
                                    <template #label>
                                        <strong>ORDEN EN EL CARRUSEL</strong>
                                    </template>
                                    <validation-provider
                                        #default="{ errors }"
                                        rules="integer"
                                        name="orden"
                                    >
                                        <b-form-input
                                            id="testimonial_order"
                                            v-model="formData.testimonial_order"
                                            type="number"
                                            min="0"
                                            placeholder="Vacío = al final ..."
                                            :state="errors.length > 0 ? false : null"
                                            trim
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                    <small class="text-muted">Menor número aparece primero.</small>
                                </b-form-group>
                            </b-col>

                        </b-row>

                        <template #footer>

                            <!-- BUTTON SUBMIT -->
                            <b-button-loading
                                text="ACTUALIZAR"
                                type="submit"
                                :processing="processing">
                            </b-button-loading>

                            <b-button
                                variant="outline-secondary"
                                class="ml-1"
                                :to="{ name: 'testimonials' }"
                            >
                                CANCELAR
                            </b-button>

                        </template>

                    </b-card>
                </b-col>
            </b-row>

        </b-form>

    </validation-observer>
</template>

<script>

    import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
    import BButtonLoading from '@core/components/b-button-loading/BButtonLoading.vue';
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { useToast } from 'vue-toastification/composition';
    import { ref } from '@vue/composition-api';
    import Ripple from 'vue-ripple-directive';
    import { required } from '@validations';
    import router from '@/router';
    import store from '@/store';
    import moment from 'moment';

    export default {
        components: {
            ValidationProvider,
            ValidationObserver,

            BButtonLoading,

            required
        },
        directives: {
            Ripple
        },
        props: {
            testimonialData: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                moment
            }
        },
        setup(props) {

            // USE TOAST
            const toast = useToast();

            // REFS
            const processing = ref(false);
            const refForm = ref(null);

            const formData = ref({
                testimonial_alias: props.testimonialData.testimonial_alias,
                testimonial_order: props.testimonialData.testimonial_order,
                is_anonymous: Boolean(props.testimonialData.is_anonymous)
            });

            const onSubmit = async () => {

                const isValid = await refForm.value.validate();

                if (!isValid)
                {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: 'Complete los campos requeridos.',
                            icon: 'AlertTriangleIcon',
                            variant: 'warning'
                        }
                    });

                    return;
                }

                processing.value = true;

                const payload = {
                    testimonial_alias: formData.value.testimonial_alias,
                    testimonial_order: formData.value.testimonial_order,
                    is_anonymous: formData.value.is_anonymous
                }

                store.dispatch('app-testimonial/updateTestimonial', { id: router.currentRoute.params.id, payload })
                    .then( response => {

                        toast({
                            component: ToastificationContent,
                            props: {
                                title: response.data.message,
                                icon: 'CheckIcon',
                                variant: 'success'
                            }
                        });

                        processing.value = false;

                    })
                    .catch( (err) => {
                        const message = err.response.data.message ? err.response.data.message : 'Error al actualizar el testimonio.';

                        toast({
                            component: ToastificationContent,
                            props: {
                                title: message,
                                icon: 'AlertTriangleIcon',
                                variant: 'danger'
                            }
                        });

                        processing.value = false;
                    });

            };

            return {
                // DATA
                processing,
                formData,

                // REFS
                refForm,

                // METHODS
                onSubmit
            }
        }
    }

</script>
